import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, ChevronRight, Clock, MapPin, Navigation, FolderOpen,
  Syringe, HeartPulse, BookOpen, Pill, AlertTriangle, FileText,
} from 'lucide-react';
import { agendaOggi, riepilogoTurno } from '@/data/mockOperatoreData';

const prestazioneIconMap = {
  'syringe': Syringe,
  'heart-pulse': HeartPulse,
  'bandage': FileText,
  'book-open': BookOpen,
  'pill': Pill,
};

const statoBadge = {
  'da-eseguire': { label: 'Da Eseguire', className: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800' },
  'in-corso': { label: 'In Corso', className: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-400 dark:border-blue-800' },
  'completata': { label: 'Completata', className: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800' },
  'annullata': { label: 'Annullata', className: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-400 dark:border-red-800' },
};

function DateSelector({ selectedDay, onDayChange }) {
  const days = [
    { key: 'ieri', label: 'Ieri' },
    { key: 'oggi', label: 'Oggi' },
    { key: 'domani', label: 'Domani' },
  ];

  const oggi = new Date();
  const dateMap = {
    ieri: new Date(oggi.getFullYear(), oggi.getMonth(), oggi.getDate() - 1),
    oggi: oggi,
    domani: new Date(oggi.getFullYear(), oggi.getMonth(), oggi.getDate() + 1),
  };

  const formatShort = (d) => {
    const dayNames = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
    return `${dayNames[d.getDay()]} ${d.getDate()}/${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  return (
    <div className="flex items-center gap-2" data-testid="date-selector">
      <Button
        variant="ghost"
        size="icon"
        className="h-9 w-9 rounded-xl text-muted-foreground"
        onClick={() => {
          const idx = days.findIndex(d => d.key === selectedDay);
          if (idx > 0) onDayChange(days[idx - 1].key);
        }}
        disabled={selectedDay === 'ieri'}
        data-testid="date-prev-btn"
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>

      <div className="flex gap-1.5">
        {days.map((d) => (
          <button
            key={d.key}
            data-testid={`date-btn-${d.key}`}
            onClick={() => onDayChange(d.key)}
            className={`
              px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200
              ${selectedDay === d.key
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
              }
            `}
          >
            <span className="block">{d.label}</span>
            <span className="block text-[10px] opacity-70 mt-0.5">{formatShort(dateMap[d.key])}</span>
          </button>
        ))}
      </div>

      <Button
        variant="ghost"
        size="icon"
        className="h-9 w-9 rounded-xl text-muted-foreground"
        onClick={() => {
          const idx = days.findIndex(d => d.key === selectedDay);
          if (idx < days.length - 1) onDayChange(days[idx + 1].key);
        }}
        disabled={selectedDay === 'domani'}
        data-testid="date-next-btn"
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  );
}

function TurnoSummary() {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="turno-summary">
      <div className="bg-muted/30 rounded-xl px-4 py-3 border border-border/40">
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Visite Oggi</p>
        <p className="text-2xl font-bold text-foreground mt-1" data-testid="visite-totali">{riepilogoTurno.visiteTotali}</p>
      </div>
      <div className="bg-emerald-50/50 dark:bg-emerald-950/20 rounded-xl px-4 py-3 border border-emerald-200/40 dark:border-emerald-800/30">
        <p className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Completate</p>
        <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300 mt-1" data-testid="visite-completate">{riepilogoTurno.visiteCompletate}</p>
      </div>
      <div className="bg-muted/30 rounded-xl px-4 py-3 border border-border/40">
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Km Stimati</p>
        <p className="text-2xl font-bold text-foreground mt-1">{riepilogoTurno.kmStimati}</p>
      </div>
      <div className="bg-muted/30 rounded-xl px-4 py-3 border border-border/40">
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Tempo Stimato</p>
        <p className="text-2xl font-bold text-foreground mt-1">{riepilogoTurno.tempoStimato}</p>
      </div>
    </div>
  );
}

function VisitCard({ visita, index }) {
  const badge = statoBadge[visita.stato] || statoBadge['da-eseguire'];
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.08 }}
    >
      <Card
        className="border-0 shadow-[0_1px_4px_rgba(0,0,0,0.06)] rounded-2xl overflow-hidden hover:shadow-[0_2px_8px_rgba(0,0,0,0.1)] transition-shadow duration-200"
        data-testid={`visit-card-${visita.id}`}
      >
        {/* Priority stripe */}
        {visita.priorita === 'alta' && (
          <div className="h-1 bg-gradient-to-r from-amber-400 to-orange-500" />
        )}

        <CardContent className="p-0">
          {/* Header: Time + Status */}
          <div className="flex items-center justify-between px-5 pt-5 pb-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-muted/50 rounded-xl px-3 py-2 border border-border/30">
                <Clock className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-foreground" data-testid={`visit-time-${visita.id}`}>
                  {visita.orarioInizio} - {visita.orarioFine}
                </span>
              </div>
              <Badge
                variant="outline"
                className={`text-xs font-medium px-2.5 py-1 rounded-lg ${badge.className}`}
                data-testid={`visit-status-${visita.id}`}
              >
                {badge.label}
              </Badge>
            </div>
            {visita.priorita === 'alta' && (
              <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-xs font-semibold">Priorita Alta</span>
              </div>
            )}
          </div>

          {/* Body: Patient + Address */}
          <div className="px-5 pb-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-primary/8 border border-primary/12 flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-primary">{visita.paziente.avatar}</span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-foreground" data-testid={`visit-patient-${visita.id}`}>
                  {visita.paziente.cognome} {visita.paziente.nome}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {visita.paziente.eta} anni &middot; {visita.codicePAI}
                </p>
                <div className="flex items-start gap-1.5 mt-2">
                  <MapPin className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
                  <p className="text-sm text-muted-foreground leading-snug">{visita.indirizzo}</p>
                </div>
              </div>
              {/* Navigate Button */}
              <Button
                variant="outline"
                size="sm"
                className="rounded-xl gap-1.5 border-border/50 text-muted-foreground hover:text-primary hover:border-primary/30 shrink-0"
                data-testid={`visit-navigate-${visita.id}`}
                onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(visita.indirizzo)}`, '_blank')}
              >
                <Navigation className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Naviga</span>
              </Button>
            </div>

            {/* Note */}
            {visita.note && (
              <div className="mt-3 px-3 py-2 rounded-xl bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200/40 dark:border-amber-800/30">
                <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
                  <span className="font-semibold">Nota:</span> {visita.note}
                </p>
              </div>
            )}
          </div>

          {/* Prestazioni */}
          <div className="px-5 pb-4">
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Prestazioni da Erogare
            </p>
            <div className="flex flex-wrap gap-2">
              {visita.prestazioni.map((p) => {
                const Icon = prestazioneIconMap[p.icona] || FileText;
                return (
                  <div
                    key={p.codice + p.descrizione}
                    className="flex items-center gap-2 bg-muted/40 rounded-xl px-3 py-2 border border-border/30"
                    data-testid={`prestazione-${visita.id}-${p.codice}`}
                  >
                    <Icon className="w-4 h-4 text-primary" />
                    <span className="text-sm text-foreground font-medium">{p.descrizione}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CTA Footer */}
          <div className="px-5 pb-5 flex justify-end">
            <Button
              className="rounded-xl gap-2 px-6 h-11 text-sm font-semibold shadow-sm"
              data-testid={`visit-start-btn-${visita.id}`}
              onClick={() => navigate(`/operatore/visita/${visita.id}`)}
            >
              <FolderOpen className="w-4 h-4" />
              Apri Fascicolo e Inizia Visita
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export default function HealthWorkerAgenda() {
  const [selectedDay, setSelectedDay] = useState('oggi');

  const visite = selectedDay === 'oggi' ? agendaOggi : [];

  return (
    <div className="space-y-6" data-testid="health-worker-agenda">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground" data-testid="agenda-title">
            Agenda Giornaliera
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Il tuo turno di oggi &middot; {riepilogoTurno.visiteTotali} accessi programmati
          </p>
        </div>
        <DateSelector selectedDay={selectedDay} onDayChange={setSelectedDay} />
      </div>

      {/* Riepilogo Turno */}
      <TurnoSummary />

      {/* Visit List */}
      <AnimatePresence mode="wait">
        {visite.length > 0 ? (
          <motion.div
            key={selectedDay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
            data-testid="visit-list"
          >
            {visite.map((visita, i) => (
              <VisitCard key={visita.id} visita={visita} index={i} />
            ))}
          </motion.div>
        ) : (
          <motion.div
            key="empty"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
          >
            <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl">
              <CardContent className="py-16 flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-2xl bg-muted/50 flex items-center justify-center mb-4">
                  <Clock className="w-7 h-7 text-muted-foreground/50" />
                </div>
                <p className="text-base font-semibold text-foreground">Nessun accesso programmato</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedDay === 'ieri' ? 'Non ci sono visite registrate per ieri.' : 'Non ci sono visite in agenda per domani.'}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
